import {
	TripettoNode,
	TripettoSlot,
	TripettoButton,
	TripettoOption,
	TripettoCheckbox
} from '../utils/tripetto-interfaces';
import { convertMarkdownToHtml } from '../utils/markdown-converter';
import { convertApiObject } from '../utils/expression-converter';
import { convertInterpolationReferences } from '../utils/interpolation-converter';

/**
 * Convert Tripetto node and slot properties to Formly field props
 */
export function convertProps(
	node: TripettoNode,
	slot?: TripettoSlot,
	slotLookup?: Map<string, { alias?: string; nodeName?: string; slotId?: string }>
): any {
	const props: any = {};
	const block = node.block;

	// Basic props - Map Tripetto properties to Formly properties correctly
	// Tripetto convention: name and description are instructions, label is the mat-label
	// Convert markdown to HTML since formly uses [innerHtml] binding
	// Also convert @slotId references to {{fieldKey}} for Formly interpolation
	let name = node.nameVisible !== false ? node.name : undefined;
	let description = node.description;

	// Convert interpolation references if slotLookup is available
	if (slotLookup) {
		name = convertInterpolationReferences(name, slotLookup);
		description = convertInterpolationReferences(description, slotLookup);
	}

	// Convert markdown to HTML
	props.name = convertMarkdownToHtml(name);
	props.label = block?.label || block?.labelContent;
	props.placeholder = block?.placeholder;
	props.description = convertMarkdownToHtml(description);
	props.required = slot?.required || false;
	props.disabled = node.disabled || false;

	// Handle different block types
	const blockType = block?.type;

	if (blockType === 'tripetto-block-text') {
		props.inputType = 'text';
		props.pattern = block.pattern;
		props.maxlength = block.maxLength;
		props.minlength = block.minLength;
	} else if (blockType === 'tripetto-block-number') {
		props.inputType = 'number';
		props.min = block.min;
		props.max = block.max;
	} else if (blockType === 'tripetto-block-email') {
		props.inputType = 'email';
		// Add email validation pattern to match Tripetto's Validators.email
		// Using a standard email regex pattern that matches Angular's Validators.email
		if (!props.pattern) {
			props.pattern = String.raw`^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$`;
		}
		props.minlength = block.minLength;
	} else if (blockType === 'tripetto-block-password') {
		props.inputType = 'password';
	} else if (blockType === 'tripetto-block-url') {
		props.inputType = 'url';
		// Add URL validation pattern - simplified for compatibility with /v flag
		// Validates http/https/ftp protocols with domain or IP address
		if (!props.pattern) {
			props.pattern = String.raw`^(?:https?|ftp):\/\/(?:\S+(?::\S*)?@)?(?:(?:[a-z0-9](?:[a-z0-9\-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9\-]{0,61}[a-z0-9])?|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d{2,5})?(?:\/\S*)?$`;
		}
		props.minlength = block.minLength;
	} else if (blockType === 'tripetto-block-phone-number') {
		props.inputType = 'tel';
		// Add phone number validation pattern to match Tripetto's IS_PHONE_NUMBER regex
		// This pattern handles various formats: (555) 123-4567, 555-123-4567, +1 555 123 4567, etc.
		if (!props.pattern) {
			props.pattern = String.raw`^(\+\d{1,2}\s?)?1?-?\.?\s?\(?\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4}$`;
		}
	} else if (blockType === 'tripetto-block-textarea') {
		props.rows = block.rows;
		props.cols = block.cols;
		props.maxlength = block.maxLength;
		props.minlength = block.minLength;
	} else if (
		blockType === 'tripetto-block-dropdown' ||
		blockType === 'tripetto-block-dropdown2' ||
		blockType === 'tripetto-block-radiobuttons' ||
		blockType === 'tripetto-block-radiobuttons2' ||
		blockType === 'tripetto-block-radiobuttons3'
	) {
		// Handle API-driven options
		if (block.apiObject?.url) {
			props.api = convertApiObject(block.apiObject);
		} else if (block.buttons || block.options) {
			// Static options - use value if set (non-empty string), otherwise fall back to name
			// Empty string, null, and undefined all indicate no value was set
			props.options = (block.buttons || block.options).map((btn: TripettoButton | TripettoOption) => ({
				label: btn.name,
				value: btn.value || btn.name
			}));
		}

		if (blockType === 'tripetto-block-dropdown' || blockType === 'tripetto-block-dropdown2') {
			props.multiple = false;
		}

		// Set layout for radio buttons based on number of options
		if (
			blockType === 'tripetto-block-radiobuttons' ||
			blockType === 'tripetto-block-radiobuttons2' ||
			blockType === 'tripetto-block-radiobuttons3'
		) {
			let shouldUseColumn = false;

			if (block.apiObject?.url) {
				// API-driven: default to column since we can't know the count
				// Most API calls return more than 2 items (states, categories, etc.)
				shouldUseColumn = true;
			} else if (block.buttons || block.options) {
				// Static options: count them
				const optionCount = (block.buttons || block.options).length;
				shouldUseColumn = optionCount > 2;
			}

			if (shouldUseColumn) {
				props.layout = {
					fxLayout: 'column',
					fxLayoutGap: '8px'
				};
			}
		}
	} else if (blockType === 'ahs-block-chips') {
		// Chips component expects an array of chip values, not options
		if (block.buttons) {
			// Set defaultChips to array - use value if set (non-empty string), otherwise fall back to name
			props.defaultChips = block.buttons.map((btn: TripettoButton) => btn.value || btn.name);
		}
		props.allowRemove = true; // Allow chips to be removed by default
	} else if (blockType === 'tripetto-block-checkbox') {
		// Single checkbox - mat-formly-check expects options even for a single checkbox
		// Create a single option with the checkbox label from node.name
		props.single = true; // Store as boolean (true/null) instead of array ([true]/[])
		props.options = [
			{
				label: props.name || node.name || 'Checkbox',
				value: true // Boolean value for single checkbox
			}
		];
		// Clear the name since it's now in the option label
		props.name = undefined;
	} else if (blockType === 'tripetto-block-checkboxes' || blockType === 'tripetto-block-checkboxes2') {
		// Multiple checkboxes - need options or API
		props.multiple = true;

		// Handle API-driven options
		if (block.apiObject?.url) {
			props.api = convertApiObject(block.apiObject);
		} else if (block.checkboxes) {
			// Static options from block.checkboxes
			// Use value if set (non-empty string), otherwise fall back to name
			props.options = block.checkboxes.map((cb: TripettoCheckbox) => ({
				label: cb.name,
				value: cb.value || cb.name
			}));
		}
	} else if (blockType === 'tripetto-block-yes-no') {
		// Yes/No converts to radio with two options
		props.options = [
			{ label: 'Yes', value: 'yes' },
			{ label: 'No', value: 'no' }
		];
	} else if (blockType === 'tripetto-block-hidden-field') {
		// Hidden fields can have fixed values or dynamic values
		if (block.fieldType === 'value' && block.fieldValue) {
			props.fixedValue = block.fieldValue;
		} else if (block.fieldType === 'variable' && block.fieldVariable) {
			// Store reference to variable (may need manual handling)
			props.api = {
				objectPath: block.fieldObjectProperties || `[${block.fieldVariable}]`
			};
		}
	} else if (blockType === 'tripetto-block-date' || blockType === 'tripetto-block-datetime') {
		props.touchUi = false;
		props.minDate = block.minDate;
		props.maxDate = block.maxDate;
	}

	// Remove undefined values
	Object.keys(props).forEach((key) => props[key] === undefined && delete props[key]);

	return props;
}
